/** Minimal rectangle shape, compatible with DOMRect. */
export interface AnchorRect {
  top: number;
  left: number;
  right: number;
  bottom: number;
}

export interface BoxSize {
  width: number;
  height: number;
}

export type TooltipSide = "bottom" | "top";

export interface TooltipPlacement {
  /** Viewport (position: fixed) coordinates of the body's top-left corner. */
  top: number;
  left: number;
  /** Which side of the anchor the body ended up on, after any flip. */
  side: TooltipSide;
  /** Arrow centre, in px from the body's left edge, so it keeps pointing at the anchor after clamping. */
  arrowLeft: number;
}

/** Gap between the anchor and the body (leaves room for the arrow). */
export const TOOLTIP_GAP = 10;
/** Minimum distance kept from every viewport edge. */
export const TOOLTIP_MARGIN = 8;
/** The arrow never sits closer than this to a body corner (the corner radius plus half the arrow). */
const ARROW_INSET = 14;

/**
 * Places a tooltip body relative to its anchor so that it never leaves the viewport: centred
 * horizontally on the anchor, then clamped to the viewport; below the anchor by default, flipped
 * above when there is no room below but there is above. Pure, so it is unit-tested without a DOM.
 */
export function placeTooltip(
  anchor: AnchorRect,
  body: BoxSize,
  viewport: BoxSize,
  prefer: TooltipSide = "bottom",
): TooltipPlacement {
  const anchorCenter = (anchor.left + anchor.right) / 2;
  const maxLeft = Math.max(TOOLTIP_MARGIN, viewport.width - body.width - TOOLTIP_MARGIN);
  const left = clamp(anchorCenter - body.width / 2, TOOLTIP_MARGIN, maxLeft);

  const below = anchor.bottom + TOOLTIP_GAP;
  const above = anchor.top - TOOLTIP_GAP - body.height;
  const fitsBelow = below + body.height <= viewport.height - TOOLTIP_MARGIN;
  const fitsAbove = above >= TOOLTIP_MARGIN;
  const side: TooltipSide =
    prefer === "bottom" ? (fitsBelow || !fitsAbove ? "bottom" : "top") : fitsAbove || !fitsBelow ? "top" : "bottom";
  const top = side === "bottom" ? below : above;

  const arrowLeft = clamp(anchorCenter - left, ARROW_INSET, Math.max(ARROW_INSET, body.width - ARROW_INSET));
  return { top, left, side, arrowLeft };
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}
